import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Tabs, Form, Input, InputNumber, Select, DatePicker, Button, Table, Modal, Popconfirm, notification, Typography, Spin, Tag, Space, Alert } from 'antd';
import { SaveOutlined, RiseOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import moment from 'moment';

import { useAuth } from '../../authentication/AuthProvider';
import request from '../../lib/request';
import { ROLE_OPTIONS, STATUS_COLORS } from './UsersPage';
import { currentRate } from './DesignationsPage';

import { date as fmtDate } from '../../lib/format';
import { CountrySelect } from '../../components/CountrySelect';
const toDate = (v) => (v ? moment(v) : null);
const fromDate = (v) => (v ? v.format('YYYY-MM-DD') : null);
const money = (v) => (v === null || v === undefined ? '-' : `EUR ${Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const DATE_FIELDS = ['date_of_birth', 'work_permit_expiry', 'hire_date', 'termination_date'];

/** One staff member: personal details, employment record, career level history with hourly cost, appraisals. */
export default function StaffDetailPage() {
  const { id } = useParams();
  const auth = useAuth();
  const isManager = auth.user.role === 'SYSTEM_ADMIN' || auth.user.role === 'COMPANY_MANAGER';
  const [user, setUser] = React.useState(null);
  const [departments, setDepartments] = React.useState([]);
  const [levels, setLevels] = React.useState([]);
  const [saving, setSaving] = React.useState(false);
  const [promoteOpen, setPromoteOpen] = React.useState(false);
  const [appraisalOpen, setAppraisalOpen] = React.useState(false);
  const [form] = Form.useForm();
  const [promoteForm] = Form.useForm();
  const [appraisalForm] = Form.useForm();

  const apply = (r) => {
    if (r.status) {
      setUser(r.data);
      const values = { ...r.data };
      for (const f of DATE_FIELDS) values[f] = toDate(r.data[f]);
      form.setFieldsValue(values);
    } else {
      notification.error({ message: r.message });
    }
    return r;
  };

  React.useEffect(() => {
    request
      .get(`/users/${id}`)
      .then((r) => r.json())
      .then((r) => {
        apply(r);
        if (r.status && r.data.company_id) {
          const q = { company_id: r.data.company_id };
          request.get('/departments', q).then((x) => x.json()).then((x) => x.status && setDepartments(x.data));
          request.get('/designations', q).then((x) => x.json()).then((x) => x.status && setLevels(x.data));
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const call = (method, path, body) => request[method](path, body).then((r) => r.json()).then(apply);

  const handleSave = () => {
    form.validateFields().then((values) => {
      const payload = { ...values };
      for (const f of DATE_FIELDS) payload[f] = fromDate(values[f]);
      delete payload.email;
      delete payload.role;
      delete payload.designation_id;
      setSaving(true);
      call('put', `/users/${id}`, payload)
        .then((r) => r.status && notification.success({ message: 'Staff record saved' }))
        .finally(() => setSaving(false));
    });
  };

  const handlePromote = () => {
    promoteForm.validateFields().then((values) => {
      call('post', `/users/${id}/career-levels`, { ...values, effective_date: fromDate(values.effective_date) }).then((r) => {
        if (r.status) {
          notification.success({ message: 'Career level recorded' });
          setPromoteOpen(false);
        }
      });
    });
  };

  const handleAppraisal = () => {
    appraisalForm.validateFields().then((values) => {
      call('post', `/users/${id}/appraisals`, { ...values, review_date: fromDate(values.review_date) }).then((r) => {
        if (r.status) {
          notification.success({ message: 'Appraisal recorded' });
          setAppraisalOpen(false);
        }
      });
    });
  };

  if (!user) return <Spin style={{ marginTop: 40 }} />;
  const currentLevel = user.Designation;
  const levelRow = levels.find((l) => l.id === user.designation_id);
  const standardRate = levelRow ? currentRate(levelRow) : null;
  const latestHistory = (user.UserCareerLevels || [])[0];
  const effectiveCost = latestHistory && latestHistory.hourly_cost_override !== null && latestHistory.hourly_cost_override !== undefined ? latestHistory.hourly_cost_override : standardRate?.hourly_rate;

  const personal = (
    <Form form={form} layout="vertical" style={{ maxWidth: 760 }} disabled={!isManager}>
      <Typography.Title level={5}>Personal details</Typography.Title>
      <Space style={{ display: 'flex' }} align="start" wrap>
        <Form.Item name="salutation" label="Salutation" style={{ width: 110 }}>
          <Select allowClear options={['MR', 'MRS', 'MS', 'DR'].map((v) => ({ value: v, label: v.charAt(0) + v.slice(1).toLowerCase() + '.' }))} />
        </Form.Item>
        <Form.Item name="name" label="Full name" rules={[{ required: true }]} style={{ width: 300 }}>
          <Input />
        </Form.Item>
        <Form.Item name="date_of_birth" label="Date of birth" style={{ width: 160 }}>
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="nationality" label="Nationality" style={{ width: 200 }}>
          <CountrySelect />
        </Form.Item>
      </Space>
      <Space style={{ display: 'flex' }} align="start" wrap>
        <Form.Item name="id_passport_number" label="ID / Passport" style={{ width: 180 }}>
          <Input maxLength={60} />
        </Form.Item>
        <Form.Item name="arc_number" label="ARC" style={{ width: 160 }}>
          <Input maxLength={60} />
        </Form.Item>
        <Form.Item name="work_permit_number" label="Work permit" style={{ width: 160 }}>
          <Input maxLength={60} />
        </Form.Item>
        <Form.Item name="work_permit_expiry" label="Permit expires" style={{ width: 160 }}>
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
      </Space>
      <Space style={{ display: 'flex' }} align="start" wrap>
        <Form.Item name="si_number" label="Social Insurance no." style={{ width: 180 }}>
          <Input maxLength={60} />
        </Form.Item>
        <Form.Item name="tin" label="TIN" style={{ width: 160 }}>
          <Input maxLength={60} />
        </Form.Item>
        <Form.Item name="phone" label="Telephone" style={{ width: 160 }}>
          <Input maxLength={40} />
        </Form.Item>
        <Form.Item name="email" label="Email (login)" style={{ width: 200 }}>
          <Input disabled />
        </Form.Item>
      </Space>
      <Form.Item name="address" label="Address">
        <Input.TextArea rows={2} />
      </Form.Item>
      <Space style={{ display: 'flex' }} align="start" wrap>
        <Form.Item name="marital_status" label="Marital status" style={{ width: 160 }}>
          <Select allowClear options={['SINGLE', 'MARRIED', 'DIVORCED', 'WIDOWED'].map((v) => ({ value: v, label: v.charAt(0) + v.slice(1).toLowerCase() }))} />
        </Form.Item>
        <Form.Item name="children_count" label="Children" style={{ width: 100 }}>
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="emergency_contact_name" label="Emergency contact" style={{ width: 200 }}>
          <Input maxLength={120} />
        </Form.Item>
        <Form.Item name="emergency_contact_phone" label="Their telephone" style={{ width: 150 }}>
          <Input maxLength={40} />
        </Form.Item>
        <Form.Item name="emergency_contact_relationship" label="Relationship" style={{ width: 130 }}>
          <Input maxLength={60} />
        </Form.Item>
      </Space>
      <Space style={{ display: 'flex' }} align="start" wrap>
        <Form.Item name="degree" label="Degree" style={{ width: 360 }}>
          <Input maxLength={255} />
        </Form.Item>
        <Form.Item name="professional_qualification" label="Professional qualification" style={{ width: 360 }}>
          <Input maxLength={255} placeholder="e.g. ACCA, ACA, CPA" />
        </Form.Item>
      </Space>
      {isManager && (
        <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
          Save
        </Button>
      )}
    </Form>
  );

  const employment = (
    <Form form={form} layout="vertical" style={{ maxWidth: 760 }} disabled={!isManager}>
      <Typography.Title level={5}>Employment details</Typography.Title>
      <Space style={{ display: 'flex' }} align="start" wrap>
        <Form.Item name="employee_code" label="Employee ID" style={{ width: 140 }}>
          <Input maxLength={30} />
        </Form.Item>
        <Form.Item name="department_id" label="Department / team / sub-team" style={{ width: 300 }}>
          <Select allowClear showSearch optionFilterProp="label" options={departments.map((d) => ({ value: d.id, label: d.name }))} />
        </Form.Item>
        <Form.Item name="employment_type" label="Employment type" style={{ width: 160 }}>
          <Select allowClear options={[{ value: 'FULL_TIME', label: 'Full-time' }, { value: 'PART_TIME', label: 'Part-time' }, { value: 'INTERNSHIP', label: 'Internship' }]} />
        </Form.Item>
        <Form.Item name="status" label="Status" style={{ width: 130 }}>
          <Select options={['ACTIVE', 'INACTIVE', 'BLOCKED'].map((v) => ({ value: v, label: v }))} />
        </Form.Item>
      </Space>
      <Space style={{ display: 'flex' }} align="start" wrap>
        <Form.Item name="hire_date" label="Date of hire" style={{ width: 160 }}>
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="termination_date" label="Date of termination" style={{ width: 160 }}>
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        {isManager && (
          <Form.Item name="gross_salary" label="Gross salary (monthly, EUR)" style={{ width: 200 }}>
            <InputNumber min={0} precision={2} style={{ width: '100%' }} />
          </Form.Item>
        )}
        <Form.Item name="financial_target" label="Financial target (EUR / year)" style={{ width: 200 }}>
          <InputNumber min={0} precision={2} style={{ width: '100%' }} />
        </Form.Item>
      </Space>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16, maxWidth: 760 }}
        message={
          currentLevel
            ? `Career level: ${currentLevel.level_order} - ${currentLevel.name}${effectiveCost !== null && effectiveCost !== undefined ? ` - hourly cost ${money(effectiveCost)}` : ' - no hourly cost set'}`
            : 'No career level yet'
        }
        description="The level and its hourly cost are history: change them on the Career level tab so timesheets before the change keep the old cost."
      />
      {isManager && (
        <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
          Save
        </Button>
      )}
    </Form>
  );

  const careerTab = (
    <div>
      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
        <Typography.Text type="secondary">Every level change with its effective date. The hourly cost comes from the level's rate history unless a personal override is set.</Typography.Text>
        {isManager && (
          <Button
            type="primary"
            icon={<RiseOutlined />}
            onClick={() => {
              promoteForm.resetFields();
              promoteForm.setFieldsValue({ effective_date: moment() });
              setPromoteOpen(true);
            }}
          >
            Record level change
          </Button>
        )}
      </Space>
      <Table
        rowKey="id"
        size="small"
        pagination={false}
        dataSource={user.UserCareerLevels || []}
        locale={{ emptyText: 'No level recorded yet' }}
        columns={[
          { title: 'Effective from', dataIndex: 'effective_date', width: 130, render: (v) => fmtDate(v) },
          { title: 'Level', key: 'level', render: (_, r) => (r.Designation ? `${r.Designation.level_order} - ${r.Designation.name}` : '-') },
          {
            title: 'Hourly cost',
            key: 'cost',
            render: (_, r) => {
              if (r.hourly_cost_override !== null && r.hourly_cost_override !== undefined) return <span>{money(r.hourly_cost_override)} <Tag>personal</Tag></span>;
              const lvl = levels.find((l) => l.id === r.designation_id);
              const rate = lvl ? currentRate(lvl, r.effective_date) : null;
              return rate ? <span>{money(rate.hourly_rate)} <Tag color="blue">level rate</Tag></span> : '-';
            },
          },
          { title: 'Note', dataIndex: 'note', render: (v) => v || '-' },
          { title: 'Recorded by', key: 'by', render: (_, r) => r.CreatedBy?.name || '-' },
          ...(isManager
            ? [
                {
                  title: '',
                  key: 'del',
                  width: 60,
                  render: (_, r) => (
                    <Popconfirm title="Remove this history entry?" onConfirm={() => call('delete', `/users/${id}/career-levels/${r.id}`)}>
                      <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  ),
                },
              ]
            : []),
        ]}
      />
    </div>
  );

  const appraisals = (
    <div>
      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
        <Typography.Text type="secondary">Appraisal scores out of 5 per review period.</Typography.Text>
        {isManager && (
          <Button
            icon={<PlusOutlined />}
            onClick={() => {
              appraisalForm.resetFields();
              appraisalForm.setFieldsValue({ review_date: moment(), period_label: `${moment().year()} ${moment().month() < 6 ? 'H1' : 'H2'}` });
              setAppraisalOpen(true);
            }}
          >
            Record appraisal
          </Button>
        )}
      </Space>
      <Table
        rowKey="id"
        size="small"
        pagination={false}
        dataSource={user.UserAppraisals || []}
        locale={{ emptyText: 'No appraisals recorded' }}
        columns={[
          { title: 'Period', dataIndex: 'period_label', width: 120 },
          { title: 'Review date', dataIndex: 'review_date', width: 130, render: (v) => fmtDate(v) },
          { title: 'Score', dataIndex: 'score', width: 100, render: (v) => <Tag color={Number(v) >= 4 ? 'green' : Number(v) >= 3 ? 'blue' : 'orange'}>{Number(v).toFixed(1)} / 5</Tag> },
          { title: 'Comments', dataIndex: 'comments', render: (v) => v || '-' },
          { title: 'Reviewer', key: 'rev', render: (_, r) => r.Reviewer?.name || '-' },
          ...(isManager
            ? [
                {
                  title: '',
                  key: 'del',
                  width: 60,
                  render: (_, r) => (
                    <Popconfirm title="Remove this appraisal?" onConfirm={() => call('delete', `/users/${id}/appraisals/${r.id}`)}>
                      <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  ),
                },
              ]
            : []),
        ]}
      />
    </div>
  );

  return (
    <div>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }} align="start">
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {user.name} <Tag color={STATUS_COLORS[user.status]}>{user.status}</Tag>
          </Typography.Title>
          <Typography.Text type="secondary">
            <Link to="/users">Staff</Link> / {ROLE_OPTIONS.find((r) => r.value === user.role)?.label || user.role}
            {user.employee_code ? ` - ${user.employee_code}` : ''}
            {user.Department ? ` - ${user.Department.name}` : ''}
            {currentLevel ? ` - ${currentLevel.name}` : ''}
          </Typography.Text>
        </div>
      </Space>

      <Tabs
        items={[
          { key: 'personal', label: 'Personal details', children: personal },
          { key: 'employment', label: 'Employment', children: employment },
          { key: 'career', label: `Career level (${(user.UserCareerLevels || []).length})`, children: careerTab },
          { key: 'appraisals', label: `Appraisals (${(user.UserAppraisals || []).length})`, children: appraisals },
        ]}
      />

      <Modal title="Record career level change" open={promoteOpen} onCancel={() => setPromoteOpen(false)} onOk={handlePromote}>
        <Form form={promoteForm} layout="vertical">
          <Form.Item name="designation_id" label="New level" rules={[{ required: true }]}>
            <Select options={levels.filter((l) => l.is_active).map((l) => ({ value: l.id, label: `${l.level_order} - ${l.name}` }))} />
          </Form.Item>
          <Form.Item name="effective_date" label="Effective from" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="hourly_cost_override" label="Personal hourly cost (EUR)" tooltip="Leave empty to use the level's standard hourly cost">
            <InputNumber min={0} precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="note" label="Note">
            <Input maxLength={255} placeholder="e.g. Promotion after 2026 H1 appraisal" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="Record appraisal" open={appraisalOpen} onCancel={() => setAppraisalOpen(false)} onOk={handleAppraisal}>
        <Form form={appraisalForm} layout="vertical">
          <Space style={{ display: 'flex' }} align="start">
            <Form.Item name="period_label" label="Period" rules={[{ required: true }]} style={{ width: 160 }}>
              <Input maxLength={40} />
            </Form.Item>
            <Form.Item name="review_date" label="Review date" rules={[{ required: true }]} style={{ width: 160 }}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="score" label="Score (0-5)" rules={[{ required: true }]} style={{ width: 120 }}>
              <InputNumber min={0} max={5} step={0.5} style={{ width: '100%' }} />
            </Form.Item>
          </Space>
          <Form.Item name="comments" label="Comments">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
